import { ApiError, type AuthSchemas } from '@ddt/api-client';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { NotAdminError } from '../errors';
import { getServices } from '../services';

export type Account = AuthSchemas['AccountResponse'];

export const useSession = defineStore('session', () => {
  const account = ref<Account | null>(null);
  const ready = ref(false);

  const authenticated = computed(() => account.value !== null);
  const isAdmin = computed(() => account.value?.roles.includes('admin') ?? false);

  function clear(): void {
    getServices().api.tokens.set(null);
    account.value = null;
  }

  /** Carrega a conta e só a aceita com a role admin: qualquer outra conta é desconectada na hora (não existe cadastro neste painel). */
  async function loadProfile(): Promise<void> {
    const { api } = getServices();
    const me = await api.auth.GET('/v1/accounts/me');
    if (!me.data) throw new ApiError(me.response.status, 'profile', 'Não foi possível carregar a conta.');
    if (!me.data.roles.includes('admin')) {
      await api.logout();
      account.value = null;
      throw new NotAdminError();
    }
    account.value = me.data;
  }

  async function login(login: string, password: string): Promise<void> {
    await getServices().api.login(login, password);
    await loadProfile();
  }

  /** Ao abrir o painel: se há sessão da aba, carrega a conta; se ela não vale mais (ou não é admin), limpa. */
  async function restore(): Promise<void> {
    try {
      if (getServices().api.tokens.get()) await loadProfile();
    } catch {
      clear();
    } finally {
      ready.value = true;
    }
  }

  async function logout(): Promise<void> {
    await getServices().api.logout();
    account.value = null;
  }

  return { account, ready, authenticated, isAdmin, login, restore, logout, clear };
});
